import { clsx } from 'clsx'
import dayjs from 'dayjs'
import { Button } from 'primereact/button'
import { useTranslation } from 'react-i18next'
import { formatPhone } from 'utils/helper/common-helpers'
import {
  formatQrMultilineText,
  getQrLookupHeaderIcon,
  isLookupBanner,
} from './qrLookupPresentation'
import { QrLookupPresentationType, type QrLookupResult } from './types'

type LookupResultPanelProps = {
  result: QrLookupResult
  checkInLoading: boolean
  onCheckIn: () => void
  onBack: () => void
}

export function LookupResultPanel({
  result,
  checkInLoading,
  onCheckIn,
  onBack,
}: LookupResultPanelProps) {
  const { t } = useTranslation()

  const banner = isLookupBanner(result)
  const headerIcon = getQrLookupHeaderIcon(result.presentationType)

  const customer = result.customer
  const timeslotLabel = customer?.bookedTimeslot ?? result.detail?.timeslotLabel
  const guestCount =
    customer?.guestCount ??
    result.detail?.guest ??
    result.guestCount ??
    undefined
  const showCustomerDetail =
    result.presentationType !== QrLookupPresentationType.Error ||
    result.reason === 'already_used'
  const checkedInAt =
    result.presentationType === QrLookupPresentationType.Error &&
    result.reason === 'already_used'
      ? customer?.checkedInAt ?? result.checkedInAt
      : undefined

  const headerTitle = banner ? result.title : t('Confirm Reservation Details')

  return (
    <div className="scan-qr-confirm scan-qr-confirm--page">
      <div className="scan-qr-confirm__panel scan-qr-confirm__panel--page border-round-xl p-3 w-full">
        <div className="scan-qr-confirm__panel-scroll">
          <div className="flex flex-column align-items-center text-center">
            <div className="flex align-items-center gap-2">
              <div
                className={clsx(
                  'w-2rem h-2rem flex align-items-center justify-content-center border-circle scan-qr-heading-icon',
                  {
                    'scan-qr-heading-icon--error':
                      headerIcon.iconVariant === 'error',
                    'scan-qr-heading-icon--warning':
                      headerIcon.iconVariant === 'warning',
                    'scan-qr-heading-icon--neutral':
                      headerIcon.iconVariant === 'neutral',
                    'scan-qr-heading-icon--info':
                      headerIcon.iconVariant === 'info',
                  }
                )}
                aria-hidden
              >
                <i
                  className={clsx(
                    headerIcon.iconClass,
                    'text-white scan-qr-heading-icon__glyph'
                  )}
                />
              </div>
              <h3 className="m-0 text-lg font-bold text-white line-height-3">
                {headerTitle}
              </h3>
            </div>

            {banner ? (
              <div className="flex flex-column gap-1 mt-2 px-2 w-full text-center justify-content-center align-items-center">
                {result.subtitle ? (
                  <p className="scan-qr-confirm__subtitle m-0 text-sm line-height-3 text-center">
                    {result.subtitle}
                  </p>
                ) : null}
                {result.message && !(customer && showCustomerDetail) ? (
                  <div className="p-3 bg-white border-round-xl mt-3">
                    <p className="scan-qr-confirm__subtitle scan-qr-confirm__message-multiline text-gray-600 m-0 text-sm line-height-3">
                      {formatQrMultilineText(result.message)}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="scan-qr-confirm__subtitle m-0 mt-1 text-sm line-height-3 px-2">
                {t('Would you like to check in with the following details?')}
              </p>
            )}
          </div>

          {customer && showCustomerDetail ? (
            <div className="scan-qr-confirm__card border-round-xl p-4 mt-3">
              {result.message ? (
                <p className="text-center w-full text-gray-600 m-0 text-sm line-height-3 mb-3">
                  {formatQrMultilineText(result.message)}
                </p>
              ) : null}
              <div className="mb-0">
                <div className="scan-qr-confirm__card-label mb-1">
                  {t('Name')}
                </div>
                <div className="scan-qr-confirm__card-name">
                  {customer.name}
                </div>
                {customer.furigana ? (
                  <div className="scan-qr-confirm__card-furigana mt-1">
                    {customer.furigana}
                  </div>
                ) : null}
              </div>

              <div className="scan-qr-confirm__card-divider" />

              <div className="scan-qr-confirm__meta-row flex gap-4">
                {timeslotLabel ? (
                  <div className="flex-1 min-w-0">
                    <div className="scan-qr-confirm__card-label mb-1">
                      {t('Time slot')}
                    </div>
                    <div className="scan-qr-confirm__card-value white-space-normal">
                      {timeslotLabel}
                    </div>
                  </div>
                ) : null}
                {guestCount != null ? (
                  <div className="flex-1 min-w-0">
                    <div className="scan-qr-confirm__card-label mb-1">
                      {t('Registrations')}
                    </div>
                    <div className="scan-qr-confirm__card-value">
                      {guestCount} {t('guest(s)')}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="scan-qr-confirm__card-divider" />

              <div className="mb-3">
                <div className="scan-qr-confirm__card-label mb-1">
                  {t('Email')}
                </div>
                <div className="scan-qr-confirm__card-value white-space-normal scan-qr-confirm__email-value">
                  {customer.email}
                </div>
              </div>
              <div className={checkedInAt ? 'mb-3' : undefined}>
                <div className="scan-qr-confirm__card-label mb-1">
                  {t('Phone number')}
                </div>
                <div className="scan-qr-confirm__card-value">
                  {formatPhone(customer.phoneNumber)}
                </div>
              </div>

              {checkedInAt ? (
                <div className="scan-qr-checked-in-time p-3 border-round-lg">
                  <div className="scan-qr-checked-in-time__label mb-1">
                    {t('Checked-in Time')}
                  </div>
                  <div className="scan-qr-checked-in-time__value">
                    {dayjs(checkedInAt).format('HH:mm')}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="scan-qr-confirm__panel-actions flex flex-column gap-3">
          {result.showCheckIn ? (
            <Button
              type="button"
              label={t('Check In')}
              className="scan-qr-accent-btn scan-qr-confirm__primary-btn w-full font-bold"
              loading={checkInLoading}
              onClick={onCheckIn}
            />
          ) : null}
          <Button
            type="button"
            label={t('Scan Next')}
            className="scan-qr-reject__scan-next w-full font-bold"
            onClick={onBack}
            disabled={checkInLoading}
          />
        </div>
      </div>
    </div>
  )
}
